import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from '../../lib/utils';

export type TextLinkTone = 'default' | 'inherit' | 'sentence' | 'notice' | 'destructive' | 'quiet';

// Split so that no tone inherits a decoration it immediately overrides: every
// tone but `quiet` wants the weight and the offset, and `notice` underlines at
// rest and so must not also carry the hover rule the others are built on.
const SHARED_CLASS = 'font-medium underline-offset-2';
const HOVER_UNDERLINE_CLASS = 'hover:underline focus-visible:underline';

/**
 * The single style source for every text-shaped link and link-lookalike in
 * the app. `tone` is the one axis rather than a free-form colour override,
 * because `cn()` is a plain filter-and-join: two colour utilities passed side
 * by side would resolve by CSS source order, not by intent.
 *
 * Green (`text-primary`) now means "this is its own thing you can click" -
 * it stops being decoration and starts marking a link that stands alone.
 * Deciding which tone a call site needs is two questions in sequence:
 * (1) does the link stand alone, or does it sit inside a running sentence?
 * (2) does the surrounding surface already own its own text colour (an
 * Alert, a coloured status box), or is it the app's ordinary background?
 *
 *   default     - a link that IS its own destination: a card-row value, a
 *                 line-item link, the linked source document on a journal's
 *                 "Source:" row. Stands alone, ordinary surface. Green at
 *                 rest, hover-only underline. The tone a call site gets by
 *                 passing nothing, because it is the majority case.
 *   inherit     - a link that stands alone but sits on a surface that
 *                 already owns its own text colour - the auth-footer legal
 *                 links, a "Back to X" link. No colour class at all, so it
 *                 reads as that surface's text; hover-only underline.
 *   sentence    - a link woven into a running sentence on the app's
 *                 ordinary background - "Click to attach" inside a document
 *                 uploader's instructions, the client named inside "Held for
 *                 <client>" on a trust bank account. Charcoal at rest,
 *                 hover-only underline - green here would compete with every
 *                 other green accent on the page for a link that is only
 *                 part of what the sentence is saying.
 *   notice      - a link inline in a sentence INSIDE an Alert or coloured
 *                 status box, where the surrounding words are already the
 *                 colour the link would be. No colour class; carries a
 *                 rest-state underline instead of hover-only, because colour
 *                 cannot carry the affordance when nothing around the link
 *                 is a different colour to it.
 *   destructive - a standalone void/undo action (the payment "Undo" text
 *                 buttons). Charcoal at rest, red on hover. Kept off green
 *                 deliberately: these sit at equal weight beside other row
 *                 controls, and a green resting state would read as an
 *                 inviting primary action rather than the muted, "you
 *                 probably don't want this" affordance a void action needs.
 *   quiet       - the lesser option beside a sibling, or a control that backs
 *                 out of a state ("Clear filters", Cancel beside Save). Muted at
 *                 rest, green on hover, regular weight, never underlined: it
 *                 must not compete with the action it stands beside.
 *
 * An exhaustive `Record` rather than an if-chain, matching the sibling
 * variant primitives (`Button.tsx`, `Badge.tsx`) - a further `TextLinkTone`
 * member then fails to compile here instead of silently falling through to
 * the last branch's treatment.
 *
 * `destructive` and `quiet` carry their own `transition-colors`: they are the
 * tones whose colour changes on hover (the others only toggle the underline,
 * which CSS animates for free), so they have a snap to smooth out.
 */
const TEXT_LINK_TONE_CLASS: Record<TextLinkTone, string> = {
  default: cn(SHARED_CLASS, HOVER_UNDERLINE_CLASS, 'text-primary'),
  inherit: cn(SHARED_CLASS, HOVER_UNDERLINE_CLASS),
  sentence: cn(SHARED_CLASS, HOVER_UNDERLINE_CLASS, 'text-secondary'),
  notice: cn(SHARED_CLASS, 'underline'),
  destructive: cn(SHARED_CLASS, HOVER_UNDERLINE_CLASS, 'text-secondary transition-colors hover:text-error'),
  quiet: 'text-text-muted no-underline transition-colors hover:text-primary',
};

export function textLinkClass(tone: TextLinkTone = 'default'): string {
  return TEXT_LINK_TONE_CLASS[tone];
}

// Disabled treatment shared with <Button>, so a disabled TextButton needs no
// utilities of its own at the call site.
const DISABLED_CLASS = 'disabled:pointer-events-none disabled:opacity-50';

interface TextLinkOwnProps {
  tone?: TextLinkTone;
  className?: string;
}

type TextLinkToProps = TextLinkOwnProps & { href?: never } & Omit<LinkProps, 'className' | 'to'> & {
    to: LinkProps['to'];
    ref?: Ref<HTMLAnchorElement>;
  };

type TextLinkHrefProps = TextLinkOwnProps & { to?: never } & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    'className' | 'href'
  > & {
    href: string;
    ref?: Ref<HTMLAnchorElement>;
  };

export type TextLinkProps = TextLinkToProps | TextLinkHrefProps;

/**
 * The shared text-link component: `to` renders a react-router `Link`, `href`
 * renders a plain `<a>`. `className` on the caller side carries size/layout
 * utilities only (text-xs, ml-1, inline-block) - colour and decoration come
 * from `tone`. Focus outline comes from the global `focus-visible` base rule
 * shared by `button`, `a`, `[role="button"]` and the other interactive
 * selectors it lists, so no `focus-visible:` classes are needed at the call
 * site.
 */
export function TextLink({ tone, className, ...props }: TextLinkProps) {
  const classes = cn(textLinkClass(tone), className);

  if ('to' in props && props.to !== undefined) {
    const { to, ref, ...rest } = props as TextLinkToProps;
    return <Link to={to} ref={ref} className={classes} {...rest} />;
  }

  const { href, ref, ...rest } = props as TextLinkHrefProps;
  return <a href={href} ref={ref} className={classes} {...rest} />;
}

interface TextButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'type'> {
  tone?: TextLinkTone;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * The `<button>`-shaped sibling of `TextLink`, for a text-styled action that
 * is not navigation - a "Show more" toggle, a toast action, "+ Add account
 * split" on a bank-line reconcile panel. Always `type="button"`; carries the
 * same disabled treatment `<Button>` uses so no call site needs to repeat it.
 */
export function TextButton({ tone, className, ref, ...props }: TextButtonProps) {
  return (
    <button
      type="button"
      ref={ref}
      className={cn(textLinkClass(tone), DISABLED_CLASS, className)}
      {...props}
    />
  );
}
